import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, ErrorHandler as NgErrorHandler, importProvidersFrom, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AppComponent } from '@app/modules/main/app.component';
import { routes } from '@app/modules/main/app-routing.module';
import { AppInterceptor } from '@auth';
import { APPLICATION_TYPE, ErrorHandler } from '@core';
import { DevicesEffects, devicesReducer } from '@devices';
import { ENVIRONMENT } from '@env';
import { environment } from '@env/environment';
import { LocationsEffects, locationsReducer } from '@locations';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { popperVariation, provideTippyConfig, tooltipVariation } from '@ngneat/helipopper';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AuthEffects, authReducer, clearStateAfterLogout, ClientsManagementsEffects, clientsManagementsReducer, ClientUsersEffects, clientUsersReducer, IncidentsEffects, incidentsReducer, WebSocketEffects } from '@ngrx-store';
import { ComponentsModule } from '@ui';
import { provideHighcharts } from 'highcharts-angular';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideToastr } from 'ngx-toastr';

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideStore({
      auth: authReducer,
      clientUsers: clientUsersReducer,
      clients: clientsManagementsReducer,
      devices: devicesReducer,
      incidents: incidentsReducer,
      locations: locationsReducer,
      router: routerReducer,
    }, {
      metaReducers: [clearStateAfterLogout],
      runtimeChecks: {
        strictActionImmutability: true,
        strictActionSerializability: false,
        strictStateImmutability: true,
        strictStateSerializability: true,
      },
    }),
    provideRouterStore(),
    provideStoreDevtools({
      connectInZone: true,
      logOnly: !isDevMode(),
      maxAge: 25,
    }),
    provideEffects([
      LocationsEffects,
      AuthEffects,
      ClientUsersEffects,
      DevicesEffects,
      IncidentsEffects,
      ClientsManagementsEffects,
      WebSocketEffects,
    ]),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    provideToastr({
      disableTimeOut: false,
      enableHtml: true,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      timeOut: 3000,
    }),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        popper: popperVariation,
        tooltip: tooltipVariation,
      },
    }),
    provideNativeDateAdapter(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(MatNativeDateModule),
    importProvidersFrom(NgIdleKeepaliveModule),
    importProvidersFrom(Keepalive),
    importProvidersFrom(ComponentsModule),
    {
      provide: APPLICATION_TYPE,
      useValue: 'desktop',
    },
    {
      provide: NgErrorHandler,
      useClass: ErrorHandler,
    },
    provideHighcharts({
      modules: () => [import('highcharts/esm/modules/no-data-to-display')],
    }),
  ],
})
  .catch(err => console.error(err));

if (environment.production) {
  enableProdMode();
}
